import { MutableRefObject, useEffect, useRef } from "react";
import { Chart } from "chart.js";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { EventStatType } from "globals/types/models.ts";
import { EnumEventStatValueType } from "globals/types/globals.ts";


function EventStatsChart(props: { eventStats: EventStatType[] }) {
  const { eventStats = [] } = props;

  const { t } = useTranslation();
  const viewChart = useRef<Chart | null>(null);
  const btnClicksChart = useRef<Chart | null>(null);

  const btnClicksChartRef = useRef<HTMLCanvasElement | null>(null);
  const viewChartRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!eventStats) return;
    if (!btnClicksChartRef.current) return;
    if (!viewChartRef.current) return;
    if (viewChart.current) {
      viewChart.current.destroy();
    }
    if (btnClicksChart.current) {
      btnClicksChart.current.destroy();
    }

    function setLineChart(ref: MutableRefObject<HTMLCanvasElement | null>, labels: string[], datasets: any[]): Chart | null {
      if (!ref.current) return null;

      return new Chart(ref.current, {
        type: "line",
        data: {
          labels: labels,
          datasets: datasets
        },
        options: {
          animation: false,
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "right"
              // display: false
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              min: 0,
              ticks: {
                stepSize: 1
              }
            },
            y: {
              beginAtZero: true,
              min: 0,
              ticks: {
                stepSize: 1
              }
            }
          }
        }
      });
    }

    const colors = [
      "rgb(255, 99, 132)",
      "rgb(54, 162, 235)",
      "rgb(255, 205, 86)",
      "rgb(75, 192, 192)",
      "rgb(153, 102, 255)",
      "rgb(231,127,24)",
      "rgb(241,99,129)",
      "rgb(49,12,213)"
    ];

    const btnClickValues: EnumEventStatValueType [] = [
      "website", "phone", "external", "share_url", "directions", "add_to_calendar", "favorite"
    ];


    const btnClickStats = eventStats.filter((eventStat: EventStatType) => {
      return eventStat.type === "btn_click";
    });

    const btnClicksDates: string[] = btnClickStats.map((eventStat: EventStatType) => {
      return dayjs(eventStat.date, "YYYY-MM-DD HH:mm:ss").format("D MMM YYYY");
    });
    const btnClicksBaseValues = btnClicksDates.map(() => 0);
    // @ts-ignore
    const btnClicksData: { [key: EnumEventStatValueType]: { dates: string[], values: number[] } } = {};

    btnClickValues.forEach((value: EnumEventStatValueType) => {
      // @ts-ignore
      btnClicksData[value] = {
        dates: [...btnClicksDates],
        values: [...btnClicksBaseValues]
      };
    });


    btnClickStats.forEach((eventStat: EventStatType, idx: number) => {
      if (!(eventStat.value in btnClicksData)) {
        return;
      }
      // @ts-ignore
      btnClicksData[eventStat.value].values[idx] = eventStat.count;
    });

    btnClicksChart.current = setLineChart(btnClicksChartRef, btnClicksDates,
      btnClickValues.map((value: EnumEventStatValueType, idx: number) => {
        return {
          label: t(`event.stats.${value}`),
          // @ts-ignore
          data: btnClicksData[value].values,
          borderColor: colors[idx],
          fill: false,
          tension: 0
        };
      })
    );


    const viewStats = eventStats.filter((eventStat: EventStatType) => {
      return eventStat.type === "view";
    });

    const viewData: { dates: string[], values: number[] } = {
      dates: [],
      values: []
    };

    viewStats.forEach((eventStat: EventStatType) => {
      viewData.dates.push(dayjs(eventStat.date, "YYYY-MM-DD HH:mm:ss").format("D MMM YYYY"));
      viewData.values.push(eventStat.count);
    });


    viewChart.current = setLineChart(viewChartRef, viewData.dates, [{
      label: t("event.stats.clicks"),
      data: viewData.values,
      borderColor: "rgb(25, 100, 126)",
      fill: false,
      tension: 0
    }]);

    return () => {
      if (viewChart.current) {
        viewChart.current.destroy();
      }
      if (btnClicksChart.current) {
        btnClicksChart.current.destroy();
      }
    };

  }, [eventStats, viewChartRef, btnClicksChartRef, t]);


  return (
    <div className="grid grid-cols-1 md:grid-cols-4 py-4 gap-6">
      <div className="col-span-3">
        <p className="font-bold pb-1">
          {t("event.numberOfClicks")}
        </p>
        <div className="h-[300px]">
          <canvas ref={btnClicksChartRef} className="w-full h-[300px]">
          </canvas>
        </div>
      </div>
      <div className="col-span-3">
        <p className="font-bold pb-1">
          {t("event.numberOfViews")}
        </p>
        <div className="h-[300px]">
          <canvas ref={viewChartRef} className="w-full h-[300px]">
          </canvas>
        </div>
      </div>
    </div>

  );
}

export default EventStatsChart;
